import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NotificationItem } from '@models/notifications';
import { nextId, restore, toStored } from '@utils/notifications';
import { MbsPopupType } from 'mbs-ui-kit';
import { ResellerAppPersistentStateService, ResellerPersistentState } from './app-persistent-state.service';
import { AuthService } from './auth';

export const MAX_NOTIFICATIONS = 50;
export const MAX_BADGE_COUNT = 9;
export const BRIEF_LENGTH = 140;

const SEVERITY_ORDER = [MbsPopupType.danger, MbsPopupType.warning, MbsPopupType.info, MbsPopupType.success];

@Injectable({ providedIn: 'root' })
export class NotificationsService {
  private persistentState = inject(ResellerAppPersistentStateService);
  private auth = inject(AuthService);

  private readonly notificationsState = signal<NotificationItem[]>(restore(this.persistentState.data.notifications));
  private readonly panelOpenState = signal(false);
  private readonly mutedState = signal(!!this.persistentState.data.notificationsMuted);

  private storedJson = JSON.stringify(this.persistentState.data.notifications || []);

  readonly notifications = this.notificationsState.asReadonly();
  readonly isPanelOpen = this.panelOpenState.asReadonly();
  readonly muted = this.mutedState.asReadonly();

  readonly unreadCount = computed(() => this.notificationsState().filter((notification) => !notification.read).length);

  readonly badgeText = computed(() => {
    const count = this.unreadCount();

    return count > MAX_BADGE_COUNT ? `${MAX_BADGE_COUNT}+` : String(count);
  });

  readonly badgeType = computed(() => {
    const unreadTypes = this.notificationsState()
      .filter((notification) => !notification.read)
      .map((notification) => notification.type);

    return SEVERITY_ORDER.find((type) => unreadTypes.includes(type)) || MbsPopupType.info;
  });

  constructor() {
    effect(() => this.persist(this.notificationsState()));
    effect(() => this.applyOwner(this.auth.resellerEmail()));

    this.persistentState.change.pipe(takeUntilDestroyed()).subscribe((state) => this.syncFromStorage(state));
  }

  add(notification: Omit<NotificationItem, 'id' | 'read'>): void {
    this.notificationsState.update((notifications) =>
      [
        {
          ...notification,
          id: nextId(notifications),
          read: ![MbsPopupType.danger, MbsPopupType.warning].includes(notification.type)
        },
        ...notifications
      ].slice(0, MAX_NOTIFICATIONS)
    );
  }

  remove(id: number): void {
    this.notificationsState.update((notifications) => notifications.filter((notification) => notification.id !== id));
  }

  clearAll(): void {
    this.notificationsState.set([]);
  }

  openPanel(): void {
    this.panelOpenState.set(true);
    this.markAllAsRead();
  }

  closePanel(): void {
    this.panelOpenState.set(false);
  }

  togglePanel(): void {
    this.panelOpenState() ? this.closePanel() : this.openPanel();
  }

  setMuted(value: boolean): void {
    this.mutedState.set(value);
    this.persistentState.data.notificationsMuted = value;
  }

  private markAllAsRead(): void {
    this.notificationsState.update((notifications) => notifications.map((notification) => ({ ...notification, read: true })));
  }

  private persist(notifications: NotificationItem[]): void {
    const stored = toStored(notifications);
    const json = JSON.stringify(stored);

    if (json === this.storedJson) {
      return;
    }

    this.storedJson = json;
    this.persistentState.data.notifications = stored;
  }

  private syncFromStorage(state: ResellerPersistentState): void {
    this.mutedState.set(!!state.notificationsMuted);

    const json = JSON.stringify(state.notifications || []);

    if (json === this.storedJson) {
      return;
    }

    this.storedJson = json;
    this.notificationsState.set(restore(state.notifications));
  }

  private applyOwner(email: string): void {
    const owner = this.persistentState.data.notificationsOwner;

    if (!email || owner === email) {
      return;
    }

    if (owner) {
      this.notificationsState.set([]);
    }

    this.persistentState.data.notificationsOwner = email;
  }
}
